import { Component, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { NzUploadModule, NzUploadFile } from 'ng-zorro-antd/upload';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';

import { NotificationService } from '@rapider/services';
import { environment } from '@rapider/environments';
import { DataSourceControllerService } from '@rapider/sdk';

import {
  IMPORT_DATA_CONFIG,
  FileAnalysisState,
  buildAiSchemaFormData,
  buildIngestV3Payload,
  updateSchemaField,
  updateSchemaRoot,
  cloneAiSchema,
  toggleFileExpansionState,
  setAllFilesExpansionState,
  isValidFieldName,
  getCombinedDataType,
  parseCombinedDataType,
  AiSchema
} from './import-data.config';

/**
 * @class ImportDataComponent
 * @description Orchestrates the AI data ingestion flow. 
 * Allows users to upload files, auto-triggers AI recommendations, provides editing UI, and pushes data to ingest-v3.
 */
@Component({
  selector: 'rapider-import-data',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzUploadModule,
    NzTableModule,
    NzInputModule,
    NzSelectModule,
    NzSwitchModule,
    NzButtonModule,
    NzModalModule
  ],
  templateUrl: './import-data.component.html',
  host: {
    class: 'block h-full w-full bg-body-background overflow-y-auto custom-scrollbar relative'
  }
})
export class ImportDataComponent {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly notificationService = inject(NotificationService);
  private readonly modalService = inject(NzModalService);
  private readonly dataSourceController = inject(DataSourceControllerService);

  public readonly config = IMPORT_DATA_CONFIG;

  // --- State Signals ---
  public fileStates = signal<FileAnalysisState[]>([]);
  public isImporting = signal<boolean>(false);
  public importSuccess = signal<boolean>(false);

  // --- Computed State ---
  public hasFiles = computed(() => this.fileStates().length > 0);

  // Separated lists prevent previously processed files from disappearing while a new file analyzes
  public processingFiles = computed(() => this.fileStates().filter(s => !s.schema || s.isAnalyzing || s.error));
  public readyFiles = computed(() => this.fileStates().filter(s => s.schema && !s.isAnalyzing && !s.error));

  public areAllExpanded = computed(() => this.readyFiles().length > 0 && this.readyFiles().every(s => s.isExpanded));

  public isReadyToImport = computed(() => {
    const states = this.fileStates();
    if (states.length === 0) return false;

    // Validate that all models have fields with acceptable camelCase or snake_case names
    const hasValidFields = states.every(s =>
      s.schema && s.schema.fields.every(f => f.skip || isValidFieldName(f.suggestedFieldName))
    );

    // Check if every file in the global queue has finished successfully
    const allProcessed = states.every(s => s.schema && !s.error && !s.isAnalyzing);
    return allProcessed && hasValidFields;
  });

  /**
   * Intercepts files added via ng-zorro upload. Modifies state immutably and auto-triggers analysis.
   * Generates a unique tracking ID to ensure safe re-uploads of the same file.
   * @param {NzUploadFile} file - The file intercepted.
   * @returns {boolean} False to stop standard auto-upload mechanism.
   */
  public beforeUpload = (file: NzUploadFile): boolean => {
    // Extract native file object securely, fallback to object casting
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const physicalFile = (file as any).originFileObj || (file as any as File);
    const id = Math.random().toString(36).substring(2, 11);

    this.fileStates.update(states => [...states, { id, file: physicalFile, isAnalyzing: false, isExpanded: true }]);

    // Auto-trigger analysis for the newly added file
    this.analyzeFileById(id);

    this.notificationService.createNotification('info', 'File Added', this.config.notifications.fileAdded);
    return false;
  };

  /**
   * Scrolls the viewport smoothly to the targeted imported file card.
   * @param {string} id - The specific file state tracking ID.
   */
  public scrollToCard(id: string): void {
    const element = document.getElementById(`card-${id}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  /**
   * Proxies template call to pure validation logic.
   * @param {string} name - Field name.
   * @returns {boolean} Is valid flag.
   */
  public isValidField(name: string): boolean {
    return isValidFieldName(name);
  }

  /**
   * Calculates dynamic template binding for Data Type select.
   * @param {string} type - Base datatype.
   * @param {boolean} isArray - Array flag.
   * @returns {string} Combined string.
   */
  public getCombinedType(type: string, isArray: boolean = false): string {
    return getCombinedDataType(type, isArray);
  }

  /**
   * Toggles the expanded/collapsed state of a file card securely via ID.
   * @param {string} id - The specific file state tracking ID.
   */
  public toggleFileExpansion(id: string): void {
    this.fileStates.update(states => states.map(s =>
      s.id === id ? toggleFileExpansionState(s) : s
    ));
  }

  /**
   * Toggles the expansion state for all imported files simultaneously.
   */
  public toggleAllExpansions(): void {
    const targetState = !this.areAllExpanded();
    this.fileStates.update(states => setAllFilesExpansionState(states, targetState));
  }

  /**
   * Handles the dropdown change event, parses it into distinct properties, and updates state.
   */
  public onDataTypeChange(id: string, fieldIdx: number, combinedValue: string): void {
    const { type, isArray } = parseCombinedDataType(combinedValue);
    this.fileStates.update(states => states.map(state => {
      if (state.id !== id || !state.schema) return state;
      return { ...state, schema: updateSchemaField(state.schema, fieldIdx, { type, isArray }) };
    }));
  }

  /**
   * Prompts user for confirmation before removing a file and its generated recommendations.
   * @param {string} id - Specific file state tracking ID to remove.
   */
  public confirmRemoveFile(id: string): void {
    this.modalService.confirm({
      nzTitle: this.config.texts.confirmRemoveTitle,
      nzContent: this.config.texts.confirmRemoveContent,
      nzOkText: 'Remove',
      nzOkType: 'primary',
      nzOkDanger: true,
      nzOnOk: () => {
        this.fileStates.update(states => states.filter(s => s.id !== id));
        this.notificationService.createNotification('info', 'File Removed', this.config.notifications.fileRemoved);
      }
    });
  }

  /**
   * Prompts user before forcing a re-analysis which destroys manual modifications.
   */
  public confirmReanalyzeFile(id: string): void {
    this.modalService.confirm({
      nzTitle: this.config.texts.confirmReanalyzeTitle,
      nzContent: this.config.texts.confirmReanalyzeContent,
      nzOkText: 'Yes, Re-analyze',
      nzOkType: 'primary',
      nzOnOk: () => this.analyzeFileById(id, true)
    });
  }

  /**
   * Executes AI Schema Recommendation call for a specific file.
   * Retains an original schema snapshot for the read-only preview.
   * @param {string} id - The specific file state tracking ID.
   * @param {boolean} force - Forces analysis even if schema exists.
   */
  private async analyzeFileById(id: string, force: boolean = false): Promise<void> {
    const currentState = this.fileStates().find(s => s.id === id);
    if (!currentState || (currentState.schema && !force)) return;

    this.fileStates.update(states => states.map(s =>
      s.id === id ? { ...s, isAnalyzing: true, error: undefined } : s
    ));

    const formData = buildAiSchemaFormData(currentState.file);

    try {
      const response = await firstValueFrom(
        this.http.post<AiSchema>(`${environment.apiBaseUrl}/data-sources/ai-schema-recommendation`, formData)
      );

      this.fileStates.update(states => states.map(s =>
        s.id === id ? {
          ...s,
          isAnalyzing: false,
          schema: response,
          originalSchema: cloneAiSchema(response)
        } : s
      ));
    } catch (error) {
      this.fileStates.update(states => states.map(s =>
        s.id === id ? { ...s, isAnalyzing: false, error: 'Analysis failed.' } : s
      ));
      this.notificationService.createNotification('error', 'Error', this.config.notifications.analyzeError);
    }
  }

  /**
   * Dispatches updates to root schema properties (Model Name, Description) using pure functions.
   */
  public updateSchemaRootProperty(id: string, property: 'modelName' | 'modelDescription', value: string): void {
    this.fileStates.update(states => states.map(state => {
      if (state.id !== id || !state.schema) return state;
      return { ...state, schema: updateSchemaRoot(state.schema, { [property]: value }) };
    }));
  }

  /**
   * Dispatches updates to specific field properties using pure functions.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public updateFieldProperty(id: string, fieldIndex: number, property: string, value: any): void {
    this.fileStates.update(states => states.map(state => {
      if (state.id !== id || !state.schema) return state;
      return { ...state, schema: updateSchemaField(state.schema, fieldIndex, { [property]: value }) };
    }));
  }

  /**
   * Initiates the final bulk ingestion process to the V3 endpoint via SDK properly structured payload.
   */
  public startImport(): void {
    if (!this.isReadyToImport()) return;

    this.isImporting.set(true);

    const readyStates = this.readyFiles();
    // Use the first file's model name, falling back to 'ImportedFiles'
    const dataSourceName = readyStates[0].schema?.modelName || 'ImportedFiles';

    // buildIngestV3Payload now returns the raw object mapping
    const payload = buildIngestV3Payload(readyStates, dataSourceName);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    // Note that payload shouldn't be wrapped in a body object as it is already structured as the expected body for ingestV3, so we can pass it directly
    this.dataSourceController.ingestV3(payload).subscribe({
      next: () => {
        this.isImporting.set(false);
        this.importSuccess.set(true);
        this.notificationService.createNotification('success', 'Success', this.config.notifications.importSuccess);
      },
      error: () => {
        this.isImporting.set(false);
        this.notificationService.createNotification('error', 'Error', this.config.notifications.importError);
      }
    });
  }

  public goToDashboard(): void {
    this.router.navigate(['/dashboard']);
  }
}